import { Button, Flex, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useCallback } from "react";

import { useDeleteMessageTemplate } from "../../api/hooks/deskCollection/messageTemplate/useDeleteMessageTemplate.ts";
import { useUpdateMessageTemplate } from "../../api/hooks/deskCollection/messageTemplate/useUpdateMessageTemplate.ts";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal.tsx";
import SectionTitle from "../../components/Section/SectionTitle/SectionTitle.tsx";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { MessageTemplate } from "../../types/deskCollection/messageTemplate.ts";

interface Props {
  template: MessageTemplate;
  refresh: () => void;
}

export const MessageTemplateItem = ({ template, refresh }: Props) => {
  const [, updateTemplate] = useMutationNotificationWrapper(
    useUpdateMessageTemplate(),
    {
      success: {
        message: "Message Template has been successfully updated",
      },
    }
  );

  const [, deleteTemplate] = useMutationNotificationWrapper(
    useDeleteMessageTemplate(),
    {
      success: {
        message: "Message Template has been successfully deleted",
      },
    }
  );

  const [
    showDeleteConfirmationModal,
    { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
  ] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      template: template.template,
    },
    validateInputOnChange: true,
    clearInputErrorOnChange: true,
  });

  const handleUpdateTemplate = useCallback(() => {
    updateTemplate({ id: template.id, input: form.values }).then((data) => {
      if (!data.error) {
        refresh();
      }
    });
  }, [form.values, template.id, refresh, updateTemplate]);

  const handleDeleteTemplate = useCallback(() => {
    deleteTemplate({ id: template.id }).then((data) => {
      if (!data.error) {
        refresh();
      }
    });
  }, [deleteTemplate, template.id, refresh]);

  return (
    <Flex gap={16} direction="column">
      <SectionTitle variant="blue">{template.name}</SectionTitle>
      <Textarea
        autosize
        required
        size="m"
        minRows={3}
        placeholder="Enter Template"
        {...form.getInputProps("template")}
      />
      <Flex gap={16} direction="row-reverse">
        <Button
          variant="filledGolden"
          size="md"
          disabled={!form.isDirty() || !form.isValid()}
          onClick={handleUpdateTemplate}
        >
          Edit
        </Button>
        <Button
          variant="outlineGolden"
          size="md"
          onClick={openDeleteConfirmationModal}
        >
          Delete
        </Button>
      </Flex>

      {showDeleteConfirmationModal && (
        <ConfirmationModal
          opened={true}
          message={"Are you sure you want to delete this message template?"}
          onConfirm={handleDeleteTemplate}
          onClose={closeDeleteConfirmationModal}
        />
      )}
    </Flex>
  );
};
